/**
 * MandatGatePage · écran bloquant « Mettez en place votre prélèvement SEPA ».
 *
 * Affiché après la 1ʳᵉ connexion quand le 1er mois est payé par carte (à la
 * souscription) mais que le mandat SEPA GoCardless récurrent n'est pas encore
 * posé (`mandateRequired`). Tant que le mandat n'est pas signé, l'app reste
 * inaccessible (gate via AuthGuard). Le 1er prélèvement SEPA démarre à M+1.
 */
import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useAuth } from "../../auth/AuthContext";
import { Logo } from "../../ui/Logo";
import { Button } from "../../ui/Button";
import { useDemoMode } from "../../lib/demo/demoMode";

const GC_DROPIN_SCRIPT =
  "https://pay.gocardless.com/billing/static/dropin/v2/initialise.js";

interface GcDropinHandler {
  open: () => void;
}
interface GcDropin {
  create: (opts: {
    billingRequestFlowID: string;
    environment: "live" | "sandbox";
    onSuccess?: () => void;
    onExit?: (error?: unknown) => void;
  }) => GcDropinHandler;
}

function loadGcDropin(): Promise<GcDropin> {
  return new Promise((resolve, reject) => {
    const w = window as unknown as { GoCardlessDropin?: GcDropin };
    if (w.GoCardlessDropin) return resolve(w.GoCardlessDropin);
    const onReady = () =>
      w.GoCardlessDropin
        ? resolve(w.GoCardlessDropin)
        : reject(new Error("Drop-in indisponible"));
    const existing = document.querySelector<HTMLScriptElement>("script[data-gc-dropin]");
    if (existing) {
      existing.addEventListener("load", onReady);
      existing.addEventListener("error", () => reject(new Error("load error")));
      return;
    }
    const s = document.createElement("script");
    s.src = GC_DROPIN_SCRIPT;
    s.async = true;
    s.dataset.gcDropin = "1";
    s.addEventListener("load", onReady);
    s.addEventListener("error", () => reject(new Error("load error")));
    document.head.appendChild(s);
  });
}

async function confirmGcWithPoll(billingRequestId: string, attempts = 8): Promise<void> {
  for (let i = 0; i < attempts; i++) {
    const r = await api.abonnementSaas.gcConfirm({ billingRequestId });
    if (r.status === "confirmed") return;
    await new Promise((res) => setTimeout(res, 1500));
  }
}

export function MandatGatePage() {
  const { status, mandateRequired, refreshMe, logout } = useAuth();
  const navigate = useNavigate();
  const demoActive = useDemoMode();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["abonnement-saas-me"],
    queryFn: () => api.abonnementSaas.me(),
  });

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Logo size={48} />
      </div>
    );
  }
  if (status === "anonymous") return <Navigate to="/login" replace />;
  // En mode démo : on n'impose pas le mandat, on laisse explorer.
  if (demoActive) return <Navigate to="/" replace />;
  if (!mandateRequired) return <Navigate to="/" replace />;

  async function start() {
    const abo = q.data;
    if (!abo || !abo.planCode) {
      setErr("Abonnement introuvable. Rechargez la page.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const m = await api.abonnementSaas.gcMandate({
        planCode: abo.planCode,
        cycle: abo.cycle,
      });
      const Dropin = await loadGcDropin();
      const handler = Dropin.create({
        billingRequestFlowID: m.flowId,
        environment: m.environment,
        onSuccess: () => {
          void finish(m.billingRequestId);
        },
        onExit: () => setBusy(false),
      });
      handler.open();
    } catch (e) {
      setErr((e as Error).message || "Impossible d'ouvrir le formulaire SEPA.");
      setBusy(false);
    }
  }

  async function finish(billingRequestId: string) {
    try {
      await confirmGcWithPoll(billingRequestId);
    } catch {
      // le webhook mandates.active prendra le relais
    }
    await refreshMe();
    navigate("/", { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-50 px-4 py-10">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl ring-1 ring-navy-100">
        <Logo size={40} />
        <h1 className="mt-6 text-2xl font-bold tracking-tight text-navy-800">
          {q.data?.cycle === "annuel"
            ? "Activez le prélèvement pour votre renouvellement"
            : "Activez votre prélèvement SEPA"}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-navy-600">
          {q.data?.cycle === "annuel"
            ? "Votre abonnement annuel arrive à échéance dans moins de 2 mois. Mettez en place votre mandat de prélèvement SEPA pour que le renouvellement se fasse automatiquement, sans coupure de service. Le prélèvement aura lieu à la date de renouvellement."
            : "Votre premier mois est réglé. Dernière étape : mettez en place votre mandat de prélèvement SEPA. Vos prochaines échéances seront alors prélevées automatiquement. Le premier prélèvement aura lieu à la prochaine période."}
        </p>
        <ul className="mt-5 space-y-2 text-sm text-navy-600">
          <li>✓ Sécurisé par GoCardless</li>
          <li>✓ Sans engagement, résiliable à tout moment</li>
          <li>✓ 2 minutes, votre IBAN suffit</li>
        </ul>
        {err ? (
          <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {err}
          </p>
        ) : null}
        <Button
          onClick={() => void start()}
          disabled={busy || q.isLoading}
          className="mt-6 w-full"
        >
          {busy ? "Ouverture…" : "Mettre en place mon prélèvement"}
        </Button>
        <button
          type="button"
          onClick={() => void logout()}
          className="mt-4 w-full text-center text-xs text-navy-400 transition-colors hover:text-navy-600"
        >
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
